import { useEffect, useRef } from "react";
import styled from "styled-components";
import {
  ActivityIcon,
  MapPinIcon,
  PaperclipIcon,
  PencilIcon,
  PlusCircleIcon,
  ShieldCheckIcon,
  Trash2Icon,
  UserCheckIcon,
  UserIcon,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Button } from "@monolith-forensics/monolith-ui";

import { monolithMoment } from "@/utils/date-format";
import { useAssetActivityLog } from "../labAssets.query";
import type { AssetActivityLogRow } from "../labAssets.types";
import type { AssetHistoryKind } from "./assetHistory.utils";
import {
  getHistoryUserName,
  parseAssetHistoryEntry,
} from "./assetHistory.utils";

const KIND_ICONS: Record<AssetHistoryKind, LucideIcon> = {
  created: PlusCircleIcon,
  issued_to: UserCheckIcon,
  location: MapPinIcon,
  updated: PencilIcon,
  deleted: Trash2Icon,
  validation: ShieldCheckIcon,
  attachment: PaperclipIcon,
  other: ActivityIcon,
};

const HistoryItem = ({ row }: { row: AssetActivityLogRow }) => {
  const entry = parseAssetHistoryEntry(row);
  const Icon = KIND_ICONS[entry.kind];

  return (
    <div className="history-item">
      <Icon size={16} className="kind-icon" />
      <div className="history-content">
        <div className="history-what">
          <span className="history-label">{entry.label}</span>
          {entry.detail && (
            <span className="history-detail">{entry.detail}</span>
          )}
        </div>
        <div className="history-meta">
          <UserIcon size={12} />
          <span>{getHistoryUserName(row.user)}</span>
          <span>·</span>
          <span>
            {monolithMoment({ timestamp: row.timestamp, includeTime: true })}
          </span>
        </div>
      </div>
    </div>
  );
};

export const AssetHistoryFeed = styled(
  ({ className, assetUuid }: { className?: string; assetUuid: string }) => {
    const {
      data,
      isLoading,
      isError,
      refetch,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
    } = useAssetActivityLog(assetUuid);
    const rows = data?.pages.flatMap((page) => page.data ?? []) ?? [];

    // A sentinel instead of a scroll handler: it fires whichever ancestor
    // scrolls, and also when the first page doesn't fill the viewport.
    const sentinelRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      const sentinel = sentinelRef.current;
      if (!sentinel || !hasNextPage || isFetchingNextPage || isError) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) fetchNextPage();
        },
        { rootMargin: "200px" }
      );
      observer.observe(sentinel);
      return () => observer.disconnect();
    }, [hasNextPage, isFetchingNextPage, isError, fetchNextPage]);

    if (isLoading) {
      return (
        <div className={className}>
          <div className="loading-indicator">Loading history...</div>
        </div>
      );
    }

    return (
      <div className={className}>
        {rows.map((row) => (
          <HistoryItem row={row} key={row.log_id} />
        ))}
        {!isError && rows.length === 0 && (
          <div className="empty-state">No history for this asset yet.</div>
        )}
        {isFetchingNextPage && (
          <div className="loading-indicator">Loading more...</div>
        )}
        {isError && (
          <div className="error-state">
            <span>Something went wrong loading the history.</span>
            <Button
              variant="outlined"
              color="primary"
              size="xs"
              onClick={() => refetch()}
            >
              Retry
            </Button>
          </div>
        )}
        <div ref={sentinelRef} />
      </div>
    );
  }
)`
  display: flex;
  flex-direction: column;
  gap: 5px;

  .history-item {
    display: flex;
    gap: 10px;
    padding: 8px 16px;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.palette.divider};
  }

  .kind-icon {
    flex-shrink: 0;
    margin-top: 1px;
    color: ${({ theme }) => theme.palette.primary.main};
  }

  .history-content {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
  }

  .history-what {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    font-size: 13px;
    color: ${({ theme }) => theme.palette.text.primary};
  }

  .history-label {
    font-weight: 600;
  }

  .history-detail {
    overflow-wrap: anywhere;
  }

  .history-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  .loading-indicator,
  .empty-state {
    display: flex;
    justify-content: center;
    padding: 24px 12px;
    font-size: 14px;
    font-style: italic;
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  .loading-indicator {
    padding: 12px;
    font-size: 12px;
  }

  .error-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 24px 12px;
    font-size: 14px;
    color: ${({ theme }) => theme.palette.text.secondary};
  }
`;
